import { useEffect, useMemo, useRef, useState } from 'react';
import type { GameComponentProps } from '../core/types';
import { getChromalDelta } from './logic';
import { isChromalPuzzle, type ChromalPuzzle } from './types';

type ChromalSavedState = {
  version: 1;
  currentLevel: number;
};

type RevealState =
  { kind: 'playing'; selectedIndex: null } | { kind: 'correct' | 'wrong'; selectedIndex: number };

function readSavedState(value: unknown): ChromalSavedState {
  if (!value || typeof value !== 'object') return { version: 1, currentLevel: 0 };
  const candidate = value as Partial<ChromalSavedState>;
  return {
    version: 1,
    currentLevel:
      Number.isInteger(candidate.currentLevel) &&
      candidate.currentLevel! >= 0 &&
      candidate.currentLevel! < 10
        ? candidate.currentLevel!
        : 0,
  };
}

export function ChromalGame({
  puzzle,
  savedState,
  disabled,
  reducedMotion,
  onStateChange,
  onComplete,
}: GameComponentProps) {
  if (!isChromalPuzzle(puzzle.payload)) {
    throw new Error('Chromal received an invalid puzzle payload.');
  }
  const chromal = puzzle.payload as ChromalPuzzle;
  const initial = useMemo(() => readSavedState(savedState), [savedState]);
  const [currentLevel, setCurrentLevel] = useState(initial.currentLevel);
  const [reveal, setReveal] = useState<RevealState>({ kind: 'playing', selectedIndex: null });
  const [status, setStatus] = useState('');
  const timeoutRef = useRef<number | null>(null);
  const level = chromal.levels[currentLevel];
  const controlsLocked = disabled || reveal.kind !== 'playing';

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    },
    [],
  );

  const choose = (index: number) => {
    if (controlsLocked) return;
    if (index === level.oddIndex) {
      setReveal({ kind: 'correct', selectedIndex: index });
      setStatus(`Correct · level ${currentLevel + 1} cleared`);
      timeoutRef.current = window.setTimeout(
        () => {
          if (currentLevel === 9) {
            onComplete({
              kind: 'solved',
              baseDeltaMs: getChromalDelta(10),
              message: 'Perfect vision!',
              metadata: { levelsCleared: 10 },
            });
            return;
          }
          const nextLevel = currentLevel + 1;
          setCurrentLevel(nextLevel);
          setReveal({ kind: 'playing', selectedIndex: null });
          setStatus(`Level ${nextLevel + 1} of 10`);
          onStateChange({ version: 1, currentLevel: nextLevel });
        },
        reducedMotion ? 60 : 300,
      );
      return;
    }

    setReveal({ kind: 'wrong', selectedIndex: index });
    setStatus(`Wrong square · the outlined square was different`);
    timeoutRef.current = window.setTimeout(
      () => {
        onComplete({
          kind: 'failed',
          baseDeltaMs: getChromalDelta(currentLevel),
          message: `${currentLevel} of 10 cleared`,
          metadata: { levelsCleared: currentLevel, selectedIndex: index },
        });
      },
      reducedMotion ? 180 : 800,
    );
  };

  return (
    <section className="chromal-game" aria-labelledby="chromal-title">
      <p className="eyebrow">Find the different lightness</p>
      <div className="chromal-heading">
        <h1 id="chromal-title">Level {currentLevel + 1}</h1>
        <span>of 10</span>
      </div>

      <ol className="chromal-progress" aria-label={`${currentLevel} of 10 levels cleared`}>
        {chromal.levels.map((_, index) => (
          <li
            key={index}
            className={index < currentLevel ? 'chromal-progress__done' : ''}
            aria-label={`Level ${index + 1}${index < currentLevel ? ', cleared' : ''}`}
          />
        ))}
      </ol>

      <div
        className={`chromal-grid chromal-grid--${reveal.kind}`}
        role="grid"
        aria-label={`Chromal level ${currentLevel + 1} color grid`}
      >
        {Array.from({ length: 6 }, (_, index) => {
          const isOdd = index === level.oddIndex;
          const isSelected = index === reveal.selectedIndex;
          const revealedOdd = reveal.kind === 'wrong' && isOdd;
          const state =
            reveal.kind === 'correct' && isSelected
              ? ', correct'
              : reveal.kind === 'wrong' && isSelected
                ? ', wrong'
                : revealedOdd
                  ? ', different square revealed'
                  : '';
          return (
            <button
              key={index}
              type="button"
              role="gridcell"
              data-grid-index={index}
              aria-label={`Row ${Math.floor(index / 2) + 1}, column ${(index % 2) + 1}${state}`}
              aria-disabled={controlsLocked}
              disabled={controlsLocked}
              className={[
                'chromal-cell',
                reveal.kind === 'correct' && isSelected ? 'chromal-cell--correct' : '',
                reveal.kind === 'wrong' && isSelected ? 'chromal-cell--wrong' : '',
                revealedOdd ? 'chromal-cell--revealed' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ backgroundColor: isOdd ? level.oddCss : level.baseCss }}
              onClick={() => choose(index)}
            />
          );
        })}
      </div>

      <p className="chromal-instruction">Tap the square with a different lightness.</p>
      <p className="chromal-status" role="status" aria-live="assertive">
        {status}
      </p>
    </section>
  );
}
